import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { CourseHeader } from "@/components/courses/CourseHeader";
import { CourseContentList } from "@/components/courses/CourseContentList";
import { CourseDescription } from "@/components/courses/CourseDescription";
import { SinglePageCourse } from "@/components/courses/SinglePageCourse";
import { useCourseDetails } from "@/hooks/useCourseDetails";
import AuthModal from "@/components/auth/AuthModal";

export default function CourseDetails({ lang = "en" }: { lang?: string }) {
  const { slug = "" } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Use the course details hook with proper typing
  const { 
    course, 
    modules, 
    lessons, 
    hasAccess, 
    setHasAccess, 
    loading, 
    isPurchased 
  } = useCourseDetails(slug, user?.id, lang);
  
  const [redirectingToLesson, setRedirectingToLesson] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-navigate to first lesson if user has access (only for multi-lesson courses)
  useEffect(() => {
    if (!loading && hasAccess && course && course.course_type !== 'single-page' && modules.length > 0 && !redirectingToLesson) {
      // Find the first module and its first lesson
      const firstModule = modules.sort((a, b) => a.position - b.position)[0];
      if (firstModule && lessons[firstModule.id]?.length > 0) {
        const firstLesson = lessons[firstModule.id].sort((a, b) => a.position - b.position)[0];
        if (firstLesson) {
                    setRedirectingToLesson(true);
          navigate(`/${lang === 'en' ? '' : lang + '/'}courses/${slug}/${firstModule.id}/${firstLesson.slug}`); 
        }
      }
    }
  }, [hasAccess, course, modules, lessons, navigate, slug, loading, redirectingToLesson, lang]);

  if (authLoading || loading) {
    return (
      <div className="container flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container py-12 text-center">
        <h2 className="text-2xl font-bold">Course not found</h2>
        <p className="mt-4">The course you're looking for doesn't exist.</p>
      </div>
    );
  }

  // Handle single-page courses
  if (course.course_type === 'single-page') {
    // For single-page courses, don't show automatic header - let markdown handle content
    return (
      <div className="container py-12">
        
        {hasAccess ? (
          <SinglePageCourse
            course={course}
            hasAccess={hasAccess}
            userId={user?.id}
            showHeader={true}
          />
        ) : (
          <CourseDescription
            content={course.content || ''}
            course={{
              ...course,
              is_free: course.is_free || false
            }}
            modules={modules}
            lessons={lessons}
            hasAccess={hasAccess}
            setHasAccess={setHasAccess}
            userId={user?.id}
            lang={lang}
          />
        )}
        
        {/* Auth Modal */}
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onOpenChange={setIsAuthModalOpen} 
          lang={lang}
        />
      </div>
    );
  }

  // Handle multi-lesson courses (existing logic)
  // Check if content has enrollment placeholder to decide whether to show header
  const hasEnrollmentPlaceholder = course.content && course.content.includes('enrollment-placeholder');
  const shouldShowHeader = !course.content || !hasEnrollmentPlaceholder;
  
  return (
    <div className="container py-12">
      {shouldShowHeader && (
        <CourseHeader
          course={{
            ...course,
            is_free: course.is_free || false
          }}
          isPurchased={isPurchased}
          lang={lang}
          hasAccess={hasAccess}
          setHasAccess={setHasAccess}
          userId={user?.id}
          setAuthModalOpen={setIsAuthModalOpen}
        />
      )}

      <CourseDescription
        content={course.content || ''}
        course={{
          ...course,
          is_free: course.is_free || false
        }}
        modules={modules}
        lessons={lessons}
        hasAccess={hasAccess}
        setHasAccess={setHasAccess}
        userId={user?.id}
        lang={lang}
      />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onOpenChange={setIsAuthModalOpen} 
        lang={lang}
      />
    </div>
  );
}
